<template>
    <div class="rental-preferences-section">
        <v-row>
            <v-col>
                <h3>Rental Preferences</h3>
            </v-col>
        </v-row>

        <!-- School & Start Date -->
        <v-row>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            School Name
                            <span v-if="!user.campus_id" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-autocomplete
                            dense
                            solo
                            flat
                            outlined
                            :value="user.campus_id"
                            @input="onCampusChange"
                            :items="sortedCampuses"
                            item-text="name"
                            item-value="id"
                            class="btn-edu-select-height"
                            hide-details
                            placeholder="Select your school"
                        >
                            <template slot="append">
                                <span>
                                    <v-icon size="18">mdi-chevron-down</v-icon>
                                </span>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Estimated Start Date
                            <span v-if="!user.university_start_date" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-menu
                            v-model="dateMenu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    :value="user.university_start_date"
                                    outlined
                                    dense
                                    v-on="on"
                                    hide-details="auto"
                                    class="btn-edu-height"
                                    placeholder="Select date"
                                >
                                    <template slot="append">
                                        <span>
                                            <v-icon size="18">mdi-calendar-blank-outline</v-icon>
                                        </span>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker
                                :value="user.university_start_date"
                                no-title
                                @input="onDateInput"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Bedrooms & Bathrooms -->
        <v-row>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Bedroom
                            <span v-if="user.preferred_bedrooms === null || user.preferred_bedrooms === undefined" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row class="px-2">
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            height="45"
                            :class="user.preferred_bedrooms === 0 ? 'field-selected' : ''"
                            :color="user.preferred_bedrooms === 0 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bedrooms: 0 })"
                            class="btn-edurent font-weight-regular"
                            block
                        >Studio</v-btn>
                    </v-col>
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            height="45"
                            :class="user.preferred_bedrooms === 1 ? 'field-selected' : ''"
                            :color="user.preferred_bedrooms === 1 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bedrooms: 1 })"
                            class="btn-edurent font-weight-regular"
                            block
                        >1</v-btn>
                    </v-col>
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            height="45"
                            :class="user.preferred_bedrooms === 2 ? 'field-selected' : ''"
                            :color="user.preferred_bedrooms === 2 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bedrooms: 2 })"
                            class="btn-edurent font-weight-regular"
                            block
                        >2</v-btn>
                    </v-col>
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            height="45"
                            :class="user.preferred_bedrooms >= 3 ? 'field-selected' : ''"
                            :color="user.preferred_bedrooms >= 3 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bedrooms: 3 })"
                            class="btn-edurent font-weight-regular"
                            block
                        >3+</v-btn>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Bathroom
                            <span v-if="user.preferred_bathrooms === null || user.preferred_bathrooms === undefined" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row class="px-2">
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            :class="user.preferred_bathrooms === 1 ? 'field-selected' : ''"
                            :color="user.preferred_bathrooms === 1 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bathrooms: 1 })"
                            class="btn-edurent font-weight-regular"
                            block
                            height="45"
                        >1</v-btn>
                    </v-col>
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            :class="user.preferred_bathrooms === 2 ? 'field-selected' : ''"
                            :color="user.preferred_bathrooms === 2 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bathrooms: 2 })"
                            class="btn-edurent font-weight-regular"
                            block
                            height="45"
                        >2</v-btn>
                    </v-col>
                    <v-col class="px-1">
                        <v-btn
                            outlined
                            :class="user.preferred_bathrooms >= 3 ? 'field-selected' : ''"
                            :color="user.preferred_bathrooms >= 3 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, preferred_bathrooms: 3 })"
                            class="btn-edurent font-weight-regular"
                            block
                            height="45"
                        >3+</v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Max Price & Pets -->
        <v-row>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Max Price
                            <span v-if="user.preferred_max_price === null || user.preferred_max_price === undefined" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" class="py-0 pt-3">
                        <v-card
                            class="pa-0 px-6 d-flex align-center"
                            outlined
                            height="45"
                        >
                            <v-slider
                                dense
                                :value="user.preferred_max_price || 0"
                                @input="$emit('update:user', { ...user, preferred_max_price: $event })"
                                step="250"
                                max="5000"
                                hide-details
                                track-color="#d8d8d8"
                            ></v-slider>
                            <span style="width: 50px" class="d-flex pl-3">${{ user.preferred_max_price || 0 }}</span>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="6">
                <v-row>
                    <v-col class="pb-0">
                        <h5>Pets <span class="optional-label">(Optional)</span></h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-btn
                            block
                            outlined
                            class="btn-edurent font-weight-regular"
                            :class="user.preferred_pet_friendly === 1 ? 'field-selected' : ''"
                            :color="user.preferred_pet_friendly === 1 ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="togglePetFriendly"
                            height="45"
                        >Pet Friendly</v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Other Amenities -->
        <v-row>
            <v-col class="pb-0">
                <h5>Other Amenities <span class="optional-label">(Optional)</span></h5>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="pb-0">
                <v-expansion-panels>
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            Choose Amenities ({{ userAmenitiesCount }} Selected)
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row>
                                <v-col
                                    cols="12"
                                    sm="6"
                                    v-for="(amenity, index) in amenities"
                                    :key="index"
                                >
                                    <v-btn
                                        outlined
                                        dense
                                        block
                                        :class="isUserAmenity(amenity) ? 'field-selected' : ''"
                                        :color="isUserAmenity(amenity) ? 'primary' : 'rgba(0,0,0,0.2)'"
                                        @click="toggleAmenity(amenity)"
                                        height="45"
                                        class="btn-edurent font-weight-regular"
                                    >
                                        {{ amenity.name }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { alphabeticalSort } from '@/utils/utils'

export default {
    name: 'RentalPreferencesSection',
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            dateMenu: false,
            sortedCampuses: []
        }
    },
    computed: {
        ...mapGetters('Campus', {
            campuses: 'campuses'
        }),
        amenities() {
            return this.$store.getters.getAmenities
        },
        userAmenitiesCount() {
            if (this.user.user_amenities) {
                return this.user.user_amenities.length
            }
            return 0
        }
    },
    methods: {
        ...mapActions('Campus', {
            getCampuses: 'getCampuses'
        }),
        onCampusChange(campusId) {
            const campus = this.campuses.find(c => c.id === campusId)
            this.$emit('update:user', {
                ...this.user,
                campus_id: campusId,
                school_id: campus ? campus.school_id : this.user.school_id
            })
        },
        onDateInput(value) {
            this.dateMenu = false
            this.$emit('update:user', { ...this.user, university_start_date: value })
        },
        togglePetFriendly() {
            this.$emit('update:user', {
                ...this.user,
                preferred_pet_friendly: this.user.preferred_pet_friendly === 0 ? 1 : 0
            })
        },
        isUserAmenity(amenity) {
            if (!this.user.user_amenities) return false
            return this.user.user_amenities.some(ua => ua.amenity_id === amenity.id)
        },
        toggleAmenity(amenity) {
            let newAmenities = [...(this.user.user_amenities || [])]

            if (this.isUserAmenity(amenity)) {
                newAmenities = newAmenities.filter(ua => ua.amenity_id !== amenity.id)
            } else {
                newAmenities.push({
                    user_id: this.user.id,
                    amenity_id: amenity.id,
                    amenity: amenity,
                    is_active: 1
                })
            }

            this.$emit('update:user', { ...this.user, user_amenities: newAmenities })
        }
    },
    async beforeMount() {
        if (this.campuses.length === 0) {
            await this.getCampuses()
        }
        this.sortedCampuses = [...this.campuses].sort(alphabeticalSort())
    }
}
</script>

<style scoped>
.rental-preferences-section h3 {
    font-size: 26px !important;
    font-weight: 700;
    color: #000;
}

.rental-preferences-section h5 {
    font-size: 14px !important;
    font-weight: 700;
    color: #000;
}

.rental-preferences-section .field-selected {
    background: rgba(45, 118, 227, 0.1);
}

.incomplete-badge {
    font-size: 10px;
    font-weight: 600;
    color: #FF9800;
    background: rgba(255, 152, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    vertical-align: middle;
}

.optional-label {
    font-size: 12px;
    font-weight: 400;
    color: #666;
}
</style>
